<template>
    <audio ref="audioEl" crossorigin="anonymous" preload="none" controls hidden></audio>
    <div class="player-fixed">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <div class="player-container" :class="{ 'player-hidden': !currentTrack }"
                        :aria-hidden="!currentTrack">
                        <div class="player">
                            <div class="player-line">
                                <div class="cover-wrap" :class="{ 'is-loading': isTrackLoading }">
                                    <img :src="currentCover" class="cover" :class="{ spinning: isPlaying, 'cover-loading': isTrackLoading }" :style="coverSpinStyle">
                                    <div class="cover-loader" :class="{ visible: isTrackLoading }" aria-hidden="true"></div>
                                    <PlayButton :isPlaying="isPlaying" @toggle="togglePlay" />
                                </div>
                                <div class="next-track-btn" v-on:click="playNextTrack()">
                                    <i class="bi bi-fast-forward-fill"></i>
                                </div>
                                <div style="margin-left: 12px; min-width: 0;">
                                    <div class="track-info">
                                        <div class="track-title">
                                            <span
                                                ref="titleTextRef"
                                                class="track-title-text"
                                                :class="{ 'is-scrollable': isTitleOverflowing }"
                                                :style="titleScrollStyle"
                                            >{{ currentTrack ? currentTrack.name : '-' }}</span>
                                        </div>
                                        <div class="artist-subtitle text_sec">{{ currentArtist || $t('common.unknown') }}</div>
                                    </div>
                                </div>
                                <div class="progress-line" ref="miniProgressRef" @click="onMiniProgressClick"
                                    :style="progressLineStyle" style="flex: 1;">
                                    <div v-if="currentWaveformUrl" :key="currentWaveformUrl"
                                        class="mini_waveform_reveal">
                                        <div class="progress-fill" :style="{ '--progress-width': progressWidth }"></div>
                                    </div>
                                </div>
                                <div style="margin-left: auto; display: flex; align-items: right; margin-right: 1rem; cursor: pointer;">
                                    <span @click="showModal = true">
                                        <i class="bi bi-chevron-up"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <SheetModal v-model="showModal">
        <div class="container" style="height:100%;">
            <div class="row justify-content-center" style="height:100%;">
                <div class="col-md-8" style="height:100%;">
                    <div class="p-container">
                        <div class="player-sec">


                            <div class="meta mb-2">
                                <div class="row">
                                    <div class="col">
                                        <span style="white-space: nowrap; overflow: hidden; font-size: 1.5rem; font-weight: 600;">{{
                                            currentTrack ?
                                                currentTrack.name : '-' }}</span><br>
                                        <span class="text_sec" style="white-space: nowrap; overflow: hidden;">{{ currentArtist || '-' }}</span>
                                    </div>
                                </div>
                                <div class="row mt-2 mb-2">
                                    <div class="col-12">
                                        <span class="chip_thing" v-if="!calculatedBpm">{{ currentTrack ? currentTrack.bpm : '-'
                                        }} BPM</span>
                                        <span class="chip_thing chip_thing_orange" v-if="calculatedBpm">{{ calculatedBpm }} BPM</span>
                                        <span v-if="(currentTrack.key != 'null') && (currentTrack.mode != 'null)')">
                                        <span class="chip_thing" v-if="!calculatedKey">{{ currentTrack ? currentTrack.key : '-'
                                        }} {{ currentTrack ? currentTrack.mode : '-' }}</span>
                                        <span class="chip_thing chip_thing_orange" v-if="calculatedKey">{{ calculatedKey }}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class="midthings">

                                <div class="mb-3">
                                    <ProgressBar :currentTime="currentTime" :duration="duration"
                                        :waveformpng="currentWaveformUrl" :bpm="currentTrack ? currentTrack.bpm : 0"
                                        :isPlaying="isPlaying" @seek="seekTo" @drag-start="handleDragStart"
                                        :lufsMomentary="lufsMomentary"
                                        :lufsShortTerm="lufsShortTerm"
                                        :lufsIntegrated="lufsIntegrated" />
                                </div>

                                <div class="buttonrow mb-2">
                                    <div class="row">
                                        <div class="col playbutton playbutton-toggle"
                                            :class="{ 'is-active': isRepeatActive || isRepeatPlaylist }"
                                            :aria-pressed="isRepeatActive || isRepeatPlaylist"
                                            v-on:click="toggleRepeat()">
                                            <i class="bi" :class="isRepeatActive ? 'bi-repeat-1' : 'bi-repeat'"></i>
                                        </div>
                                        <div class="col playbutton" v-on:click="playPreviousTrack()">
                                            <i class="bi bi-rewind-fill"></i>
                                        </div>
                                        <div class="col playbutton">
                                            <i class="bi bi-play-fill" v-if="!isPlaying" v-on:click="togglePlay()"></i>
                                            <i class="bi bi-pause-fill" v-if="isPlaying" v-on:click="togglePlay()"></i>
                                        </div>
                                        <div class="col playbutton" v-on:click="playNextTrack()">
                                            <i class="bi bi-fast-forward-fill"></i>
                                        </div>
                                        <div class="col playbutton playbutton-toggle" :class="{ 'is-active': shuffle }"
                                            :aria-pressed="shuffle" v-on:click="toggleShuffle()">
                                            <i class="bi bi-shuffle"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="edit-sec">
                            <EffectsComponent :pitch="pitch" :tempo="tempo" :gain="gain" :eq="eq" :reverbMix="reverbMix"
                                :distortion="distortion" :pan="pan" :lofiAmount="lofiAmount" :showExport="canExportCurrentTrack"
                                :showNotes="canEditCurrentTrack" :showMetadata="canEditCurrentTrack"
                                @update:pitch="pitch = $event"
                                @update:tempo="tempo = $event" @update:gain="gain = $event"
                                @update:eq:low="eq.low = $event" @update:eq:mid="eq.mid = $event"
                                @update:eq:high="eq.high = $event"
                                @update:eq:lowFrequency="eq.lowFrequency = $event"
                                @update:eq:midFrequency="eq.midFrequency = $event"
                                @update:eq:highFrequency="eq.highFrequency = $event"
                                @update:eq:lowQ="eq.lowQ = $event"
                                @update:eq:midQ="eq.midQ = $event"
                                @update:eq:highQ="eq.highQ = $event"
                                @update:reverbMix="reverbMix = $event"
                                @update:distortion="distortion = $event" @update:pan="pan = $event"
                                @update:lofiAmount="lofiAmount = $event">
                                <template #export>
                                    <TrackExportPanel :track="currentTrack" />
                                </template>
                                <template #notes>
                                    <TrackNotesPanel :track="currentTrack" @updated="handleTrackUpdated" />
                                </template>
                                <template #metadata>
                                    <TrackMetadataPanel :track="currentTrack" @updated="handleTrackUpdated" />
                                </template>
                            </EffectsComponent>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    </SheetModal>


</template>

<script>
import { LoudnessMeter } from '@domchristie/needles';
import SheetModal from '@/components/UI/SheetModal.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import PlayButton from '@/components/UI/PlayButton.vue'
import storageService from '@/scripts/services/StorageService.js'
import { settings } from '@/scripts/services/SettingsService.js';
import EffectsComponent from './EffectsComponent.vue';
import TrackExportPanel from './TrackExportPanel.vue';
import TrackNotesPanel from './TrackNotesPanel.vue';
import TrackMetadataPanel from './TrackMetadataPanel.vue';
import { playerStore, playerActions } from '@/scripts/services/PlayerService.js';
import { calculateNewKey } from '@/scripts/utils.js';
import { makeDistortionCurve, generateImpulseResponse } from '@/scripts/utils/audioUtils.js';

export default {
    name: "PlayerComponent",
    components: {
        SheetModal,
        ProgressBar,
        PlayButton,
        EffectsComponent,
        TrackExportPanel,
        TrackNotesPanel,
        TrackMetadataPanel
    },
    data() {
        return {
            // Локальные данные для audio context и обработки
            player: playerStore, // ссылка на store

            // Waveform URL for display
            currentWaveformUrl: null,

            // Audio processing nodes
            audioCtx: null,
            mediaElementSource: null,
            soundtouchNode: null,
            bitcrusherNode: null,
            leftGain: null,
            rightGain: null,
            distortionNode: null,
            convolverNode: null,
            lowShelfFilter: null,
            midPeakingFilter: null,
            highShelfFilter: null,
            dryGain: null,
            wetGain: null,
            masterGain: null,
            splitterNode: null,
            mergerNode: null,
            timeUpdateInterval: null,
            calculatedBpm: null,
            calculatedKey: null,

            // Loudness Meter
            loudnessMeter: null,
            lufsMomentary: null,
            lufsShortTerm: null,
            lufsIntegrated: null,
            isLoudnessMeterStarted: false,

            isTrackLoading: false,

            isTitleOverflowing: false,
            titleScrollDistance: 0,
            titleResizeListener: null,
            currentTrackRequestId: 0,
        }
    },
    computed: {
        // Прокси к store для удобства в шаблоне
        currentTrack() { return playerStore.currentTrack; },
        currentProject() {
            const projectId = playerStore.currentProjectId;
            if (!projectId) return null;

            return [
                ...storageService.state.projects,
                ...storageService.state.sharedProjects
            ].find(project => project.id === projectId) || null;
        },
        currentArtist() {
            return this.currentProject?.artist || this.currentTrack?.artist || '';
        },
        isPlaying() { return playerStore.isPlaying; },
        currentTime() { return playerStore.currentTime; },
        duration() { return playerStore.duration; },
        showModal: {
            get() { return playerStore.showModal; },
            set(val) { playerStore.showModal = val; }
        },
        currentCover() { return playerStore.currentCover; },
        canExportCurrentTrack() {
            return Boolean(this.currentTrack) && storageService.state.storageType !== 'shared';
        },
        canEditCurrentTrack() {
            return Boolean(this.currentTrack) && storageService.state.storageType !== 'shared';
        },
        liveLoudnessMetering() { return settings.state.liveLoudnessMetering; },
        // Effects
        pitch: {
            get() { return playerStore.pitch; },
            set(val) { playerStore.pitch = val; }
        },
        tempo: {
            get() { return playerStore.tempo; },
            set(val) { playerStore.tempo = val; }
        },
        gain: {
            get() { return playerStore.gain; },
            set(val) { playerStore.gain = val; }
        },
        eq: {
            get() { return playerStore.eq; },
            set(val) { playerStore.eq = val; }
        },
        reverbMix: {
            get() { return playerStore.reverbMix; },
            set(val) { playerStore.reverbMix = val; }
        },
        distortion: {
            get() { return playerStore.distortion; },
            set(val) { playerStore.distortion = val; }
        },
        pan: {
            get() { return playerStore.pan; },
            set(val) { playerStore.pan = val; }
        },
        lofiAmount: {
            get() { return playerStore.lofiAmount; },
            set(val) { playerStore.lofiAmount = val; }
        },

        isRepeatActive: {
            get() { return playerStore.isRepeatActive; },
            set(val) { playerStore.isRepeatActive = val; }
        },
        isRepeatPlaylist: {
            get() { return playerStore.isRepeatPlaylist; },
            set(val) { playerStore.isRepeatPlaylist = val; }
        },
        shuffle() { return playerStore.shuffle; },

        progressWidth() {
            if (this.duration === 0) return '0%';
            const percentage = (this.currentTime / this.duration) * 100;
            return `${percentage}%`;
        },
        progressLineStyle() {
            if (this.currentWaveformUrl) {
                return { '--waveform-bg': `url(${this.currentWaveformUrl})` };
            }
            return {};
        },
        coverSpinStyle() {
            // One full rotation = 2 bars (8 beats)
            // Duration in seconds = 480 / BPM (8 beats * 60 seconds / BPM)
            const bpm = this.calculatedBpm || (this.currentTrack?.bpm) || 120;
            const duration = 480 / bpm;
            return { '--spin-duration': `${duration}s` };
        },
        titleScrollStyle() {
            if (!this.isTitleOverflowing || this.titleScrollDistance <= 0) {
                return {};
            }
            const duration = Math.max(8, Math.round(this.titleScrollDistance / 5));
            return {
                '--title-scroll-distance': `${this.titleScrollDistance}px`,
                '--title-scroll-duration': `${duration}s`
            };
        }
    },
    watch: {
        pitch(newPitch) {
            if (this.soundtouchNode)
                this.soundtouchNode.parameters.get('pitch').value = newPitch;
            // Calculate new key based on pitch shift
            if ((this.currentTrack && this.currentTrack.key) && (this.currentTrack.mode != 'null') && (this.currentTrack.key != 'null')) {
                const semitonesDelta = (Math.log2(newPitch) * 12).toFixed(0);
                if(semitonesDelta == 0){
                    this.calculatedKey = null
                } else {
                    const newKey = calculateNewKey(semitonesDelta, this.currentTrack.key, this.currentTrack.mode);
                    this.calculatedKey = newKey
                }
            }
        },
        tempo(newTempo) {
            this.$refs.audioEl.preservesPitch = true;
            this.$refs.audioEl.playbackRate = newTempo;
            // Calculate new BPM based on tempo multiplier
            if (this.currentTrack && this.currentTrack.bpm) {
                const newBpm = Math.round(this.currentTrack.bpm * newTempo);
                if(newBpm == this.currentTrack.bpm){
                    this.calculatedBpm = null
                } else {
                    this.calculatedBpm = newBpm;
                }
            }
        },
        pan() {
            const pan = this.pan;
            this.leftGain.gain.value = pan <= 0 ? 1 : 1 - pan;
            this.rightGain.gain.value = pan >= 0 ? 1 : 1 + pan;
        },
        distortion() {
            this.distortionNode.curve = makeDistortionCurve(this.distortion);
        },
        'player.eq': {
            handler() {
                if (!this.lowShelfFilter || !this.midPeakingFilter || !this.highShelfFilter) {
                    return;
                }

                this.lowShelfFilter.gain.value = this.player.eq.low;
                this.lowShelfFilter.frequency.value = this.player.eq.lowFrequency ?? 320;
                this.lowShelfFilter.Q.value = this.player.eq.lowQ ?? 1;
                this.midPeakingFilter.gain.value = this.player.eq.mid;
                this.midPeakingFilter.frequency.value = this.player.eq.midFrequency ?? 1000;
                this.midPeakingFilter.Q.value = this.player.eq.midQ ?? 1;
                this.highShelfFilter.gain.value = this.player.eq.high;
                this.highShelfFilter.frequency.value = this.player.eq.highFrequency ?? 3200;
                this.highShelfFilter.Q.value = this.player.eq.highQ ?? 1;
            },
            deep: true
        },
        reverbMix() {
            this.dryGain.gain.value = 1 - this.reverbMix;
            this.wetGain.gain.value = this.reverbMix;
        },
        gain() {
            if (this.masterGain) {
                this.masterGain.gain.value = this.gain;
            }
        },
        lofiAmount(val) {
            this.bitcrusherNode.parameters.get('dryWet').value = val;
        },
        currentTrack: {
            immediate: true,
            async handler(newTrack) {
                const requestId = ++this.currentTrackRequestId;
                if (newTrack && newTrack.waveform) {
                    this.currentWaveformUrl = null;

                    // Get signed URL for waveform
                    const waveformId = typeof newTrack.waveform === 'object' ? newTrack.waveform.id : newTrack.waveform;
                    const url = await storageService.getFileLink(waveformId);
                    if (requestId !== this.currentTrackRequestId) return;

                    // Preload the waveform before displaying it.
                    const img = new Image();
                    img.onload = () => {
                        if (requestId !== this.currentTrackRequestId) return;
                        this.currentWaveformUrl = url;
                    };
                    img.src = url;
                } else {
                    this.currentWaveformUrl = null;
                }

                // Update cover based on current project
                await this.updateCurrentCover();
                if (requestId !== this.currentTrackRequestId) return;

                this.updateMediaSession();

                this._resetLoudnessValues();
                if (this.liveLoudnessMetering) {
                    this._recreateLoudnessMeter();
                    if (this.loudnessMeter && this.isPlaying) {
                        this.loudnessMeter.start();
                        this.isLoudnessMeterStarted = true;
                    }
                } else {
                    this._destroyLoudnessMeter();
                }

                this.updateTitleOverflow();
            }
        },
        currentArtist() {
            this.updateMediaSession();
        },
        isPlaying(newVal) {
            if (!this.liveLoudnessMetering) return;
            if ('mediaSession' in navigator) {
                console.log("Updating playbackState to:", newVal ? 'playing' : 'paused');
                navigator.mediaSession.playbackState = newVal ? 'playing' : 'paused';
            }
            if (this.loudnessMeter) {
                if (newVal) {
                    if (!this.isLoudnessMeterStarted) {
                        this.loudnessMeter.start();
                        this.isLoudnessMeterStarted = true;
                    } else {
                        this.loudnessMeter.resume();
                    }
                } else {
                    this.loudnessMeter.pause();
                }
            }
        },
        liveLoudnessMetering(enabled) {
            this._resetLoudnessValues();
            if (enabled) {
                this._recreateLoudnessMeter();
                if (this.loudnessMeter && this.isPlaying) {
                    this.loudnessMeter.start();
                    this.isLoudnessMeterStarted = true;
                }
            } else {
                this._destroyLoudnessMeter();
            }
        }
    },
    async mounted() {
        // Инициализируем плеер
        playerActions.init(this.$refs.audioEl);

        await this.setupAudioContext();
        playerActions.setAudioContext(this.audioCtx);

        this.setupAudioEventListeners();
        this.startTimeUpdateInterval();

        // Добавляем обработчик нажатия на пробел
        this.setupSpaceKeyListener();

        // Start loudness metering if playback is already active
        if (this.liveLoudnessMetering && this.loudnessMeter && this.isPlaying) {
            this.loudnessMeter.start();
            this.isLoudnessMeterStarted = true;
        }

        this.updateTitleOverflow();
        this.titleResizeListener = () => this.updateTitleOverflow();
        window.addEventListener('resize', this.titleResizeListener);
    },
    beforeUnmount() {
        this.currentTrackRequestId++;

        if (this.timeUpdateInterval) {
            clearInterval(this.timeUpdateInterval);
        }

        // Удаляем обработчики audio events
        this.removeAudioEventListeners();

        // Удаляем обработчик нажатия на пробел
        if (this.spaceKeyListener) {
            document.removeEventListener('keydown', this.spaceKeyListener);
            this.spaceKeyListener = null;
        }

        // Clean up Blob URLs to prevent memory leaks
        if (playerStore.currentAudioUrl && playerStore.currentAudioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(playerStore.currentAudioUrl);
            playerStore.currentAudioUrl = null;
        }

        // Clean up mediaSession handlers
        if ('mediaSession' in navigator) {
            navigator.mediaSession.setActionHandler('play', null);
            navigator.mediaSession.setActionHandler('pause', null);
            navigator.mediaSession.setActionHandler('previoustrack', null);
            navigator.mediaSession.setActionHandler('nexttrack', null);
            navigator.mediaSession.setActionHandler('seekto', null);
        }

        // Clean up audio context listener
        playerActions.cleanupAudioContext();

        this._destroyLoudnessMeter();
        this._destroyAudioGraph();

        if (this.titleResizeListener) {
            window.removeEventListener('resize', this.titleResizeListener);
            this.titleResizeListener = null;
        }
    },
    methods: {
        // Методы-проксы к actions
        togglePlay() { playerActions.togglePlay(); },
        playNextTrack() { playerActions.playNextTrack(); },
        playPreviousTrack() { playerActions.playPreviousTrack(); },
        toggleRepeat() { playerActions.toggleRepeat(); },
        toggleShuffle() { playerActions.toggleShuffle(); },
        seekTo(time) { playerActions.seekTo(time); },
        handleDragStart() { playerActions.handleDragStart(); },
        handleTrackUpdated(track) {
            playerActions.syncTrack(track);
        },
        updateTitleOverflow() {
            this.$nextTick(() => {
                const titleEl = this.$refs.titleTextRef;
                if (!titleEl || !titleEl.parentElement) {
                    this.isTitleOverflowing = false;
                    this.titleScrollDistance = 0;
                    return;
                }

                const containerWidth = titleEl.parentElement.clientWidth;
                const titleWidth = titleEl.scrollWidth;
                const distance = Math.max(0, Math.ceil(titleWidth - containerWidth));

                this.isTitleOverflowing = distance > 0;
                this.titleScrollDistance = distance;
            });
        },

        async updateCurrentCover() {
            const projectId = playerStore.currentProjectId;
            if (projectId) {
                const project = [
                    ...storageService.state.projects,
                    ...storageService.state.sharedProjects
                ].find(p => p.id === projectId);
                if (project && project.cover) {
                    const coverUrl = await storageService.getCoverUrl(project.cover);
                    playerStore.currentCover = coverUrl || '/img/nocover.png';
                    return;
                }
            }
            if (this.currentTrack?.cover) {
                const coverUrl = await storageService.getCoverUrl(this.currentTrack.cover);
                playerStore.currentCover = coverUrl || '/img/nocover.png';
                return;
            }
            playerStore.currentCover = '/img/nocover.png';
        },

        _recreateLoudnessMeter() {
            if (!this.masterGain) return;
            if (this.loudnessMeter) {
                this._resetLoudnessMeter();
                return;
            }

            this.loudnessMeter = new LoudnessMeter({
                source: this.masterGain,
                modes: ['momentary', 'short-term', 'integrated'],
                workerUri: '/worklets/needles-worker.js'
            });

            this.loudnessMeter.on('dataavailable', (event) => {
                if (event.data.mode === 'momentary') {
                    this.lufsMomentary = event.data.value;
                } else if (event.data.mode === 'short-term') {
                    this.lufsShortTerm = event.data.value;
                } else if (event.data.mode === 'integrated') {
                    this.lufsIntegrated = event.data.value;
                }
            });
        },
        _resetLoudnessValues() {
            this.lufsMomentary = null;
            this.lufsShortTerm = null;
            this.lufsIntegrated = null;
            this.isLoudnessMeterStarted = false;
        },
        _resetLoudnessMeter() {
            if (!this.loudnessMeter) return;
            if (this.loudnessMeter.state !== 'inactive') {
                this.loudnessMeter.stop();
            }
            this.loudnessMeter.reset();
        },
        _destroyLoudnessMeter() {
            if (!this.loudnessMeter) return;
            if (this.loudnessMeter.state !== 'inactive') {
                this.loudnessMeter.stop();
            }
            this.loudnessMeter.off();
            const adapter = this.loudnessMeter.workerAdapter;
            if (adapter) {
                if (adapter.worker && typeof adapter.worker.terminate === 'function') {
                    adapter.worker.terminate();
                }
                if (adapter.node) {
                    Promise.resolve(adapter.node).then((node) => {
                        if (node && typeof node.disconnect === 'function') {
                            node.disconnect();
                        }
                    });
                }
            }
            this.loudnessMeter = null;
        },
        _destroyAudioGraph() {
            const nodes = [
                this.mediaElementSource,
                this.soundtouchNode,
                this.bitcrusherNode,
                this.distortionNode,
                this.lowShelfFilter,
                this.midPeakingFilter,
                this.highShelfFilter,
                this.splitterNode,
                this.mergerNode,
                this.leftGain,
                this.rightGain,
                this.convolverNode,
                this.dryGain,
                this.wetGain,
                this.masterGain
            ];

            for (const node of nodes) {
                if (node && typeof node.disconnect === 'function') {
                    try {
                        node.disconnect();
                    } catch (error) {
                        // Already disconnected.
                    }
                }
            }

            if (this.audioCtx && this.audioCtx.state !== 'closed') {
                this.audioCtx.close().catch((error) => {
                    console.warn('Failed to close AudioContext:', error);
                });
            }

            this.mediaElementSource = null;
            this.soundtouchNode = null;
            this.bitcrusherNode = null;
            this.distortionNode = null;
            this.lowShelfFilter = null;
            this.midPeakingFilter = null;
            this.highShelfFilter = null;
            this.splitterNode = null;
            this.mergerNode = null;
            this.leftGain = null;
            this.rightGain = null;
            this.convolverNode = null;
            this.dryGain = null;
            this.wetGain = null;
            this.masterGain = null;
            this.audioCtx = null;
            playerStore.audioContext = null;
        },

        onMiniProgressClick(event) {
            if (!this.currentTrack || !this.duration) return;
            const miniProgressBar = this.$refs.miniProgressRef;
            const rect = miniProgressBar.getBoundingClientRect();
            const percent = (event.clientX - rect.left) / rect.width;
            const seekTime = percent * this.duration;
            this.seekTo(seekTime);
        },

        startTimeUpdateInterval() {
            if (this.$refs.audioEl) {
                this.timeUpdateInterval = setInterval(() => {
                    playerStore.currentTime = this.$refs.audioEl.currentTime;
                    if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
                        try {
                            navigator.mediaSession.setPositionState({
                                duration: this.duration || 0,
                                playbackRate: this.tempo || 1,
                                position: this.currentTime || 0
                            });
                        } catch (error) {
                            // Игнорируем ошибки обновления позиции
                        }
                    }

                }, 30);
            } else {
                clearInterval(this.timeUpdateInterval);
            }
        },

        async setupAudioContext() {
            const el = this.$refs.audioEl;
            if (this.audioCtx) return;

            this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

            await this.audioCtx.audioWorklet.addModule('/worklets/soundtouch-worklet.js');
            this.soundtouchNode = new AudioWorkletNode(this.audioCtx, 'soundtouch-processor');

            await this.audioCtx.audioWorklet.addModule('/worklets/bitcrusher-worklet.js');
            this.bitcrusherNode = new AudioWorkletNode(this.audioCtx, 'bitcrusher-processor');
            this.bitcrusherNode.parameters.get('dryWet').value = 0;

            this.distortionNode = this.audioCtx.createWaveShaper();
            this.distortionNode.curve = makeDistortionCurve(0);

            this.lowShelfFilter = this.audioCtx.createBiquadFilter();
            this.midPeakingFilter = this.audioCtx.createBiquadFilter();
            this.highShelfFilter = this.audioCtx.createBiquadFilter();

            this.lowShelfFilter.type = 'peaking';
            this.lowShelfFilter.frequency.value = this.player.eq.lowFrequency ?? 320;
            this.lowShelfFilter.Q.value = this.player.eq.lowQ ?? 1;
            this.lowShelfFilter.gain.value = this.player.eq.low;

            this.midPeakingFilter.type = 'peaking';
            this.midPeakingFilter.frequency.value = this.player.eq.midFrequency ?? 1000;
            this.midPeakingFilter.Q.value = this.player.eq.midQ ?? 1;
            this.midPeakingFilter.gain.value = this.player.eq.mid;

            this.highShelfFilter.type = 'peaking';
            this.highShelfFilter.frequency.value = this.player.eq.highFrequency ?? 3200;
            this.highShelfFilter.Q.value = this.player.eq.highQ ?? 1;
            this.highShelfFilter.gain.value = this.player.eq.high;

            this.convolverNode = this.audioCtx.createConvolver();
            this.convolverNode.buffer = generateImpulseResponse(this.audioCtx, 2, 2);

            this.dryGain = this.audioCtx.createGain();
            this.wetGain = this.audioCtx.createGain();
            this.masterGain = this.audioCtx.createGain();

            const src = this.audioCtx.createMediaElementSource(el);
            this.mediaElementSource = src;

            // Initialize loudness meter
            if (this.liveLoudnessMetering) {
                this._recreateLoudnessMeter();
            }


            const splitter = this.audioCtx.createChannelSplitter(2);
            const merger = this.audioCtx.createChannelMerger(2);
            this.splitterNode = splitter;
            this.mergerNode = merger;

            this.leftGain = this.audioCtx.createGain();
            this.rightGain = this.audioCtx.createGain();
            this.leftGain.gain.value = 1;
            this.rightGain.gain.value = 1;

            this.dryGain.gain.value = 1 - this.reverbMix;
            this.wetGain.gain.value = this.reverbMix;
            this.masterGain.gain.value = this.gain;
            el.volume = 1;

            src
                .connect(this.soundtouchNode)
                .connect(this.bitcrusherNode)
                .connect(this.distortionNode)
                .connect(this.lowShelfFilter)
                .connect(this.midPeakingFilter)
                .connect(this.highShelfFilter);

            this.highShelfFilter.connect(splitter);
            splitter.connect(this.leftGain, 0);
            this.leftGain.connect(merger, 0, 0);
            splitter.connect(this.rightGain, 1);
            this.rightGain.connect(merger, 0, 1);

            merger.connect(this.dryGain);
            merger.connect(this.convolverNode);
            this.convolverNode.connect(this.wetGain);

            this.dryGain.connect(this.masterGain);
            this.wetGain.connect(this.masterGain);
            this.masterGain.connect(this.audioCtx.destination);
        },
        updateMediaSession() {
            if (!('mediaSession' in navigator)) return;

            const track = this.currentTrack;
            if (!track) return;

            if('audioSession' in navigator){
                navigator.audioSession.type = 'playback'
            }

            navigator.mediaSession.metadata = new MediaMetadata({
                title: track.name || 'Unknown Track',
                artist: this.currentArtist || 'Unknown Artist',
                album: track.album || '',
                artwork: [
                    { src: this.currentCover || '/img/nocover.png', sizes: '512x512', type: 'image/png' }
                ]
            });

            // Устанавливаем обработчики действий
            navigator.mediaSession.setActionHandler('play', () => {
                console.log("MediaSession play action")
                playerActions.togglePlay();
            });

            navigator.mediaSession.setActionHandler('pause', () => {
                console.log("MediaSession pause action")
                this.togglePlay();
            });

            navigator.mediaSession.setActionHandler('previoustrack', () => {
                console.log("MediaSession previous track action")
                this.playPreviousTrack();
            });

            navigator.mediaSession.setActionHandler('nexttrack', () => {
                console.log("MediaSession next track action")
                this.playNextTrack();
            });

            navigator.mediaSession.setActionHandler('seekto', (details) => {
                console.log("MediaSession seekto action")
                if (details.seekTime) {
                    this.seekTo(details.seekTime);
                }
            });

            // Обновляем позицию воспроизведения
            if ('setPositionState' in navigator.mediaSession) {
                try {
                    navigator.mediaSession.setPositionState({
                        duration: this.duration || 0,
                        playbackRate: this.tempo || 1,
                        position: this.currentTime || 0
                    });
                } catch (error) {
                    console.log('Position state update failed:', error);
                }
            }
        },
        setupAudioEventListeners() {
            const el = this.$refs.audioEl;

            // Store handler references for cleanup
            this._audioHandlers = {
                loadstart: () => {
                    this.isTrackLoading = true;
                },
                waiting: () => {
                    this.isTrackLoading = true;
                },
                stalled: () => {
                    this.isTrackLoading = true;
                },
                canplay: () => {
                    this.isTrackLoading = false;
                },
                canplaythrough: () => {
                    this.isTrackLoading = false;
                },
                loadeddata: () => {
                    this.isTrackLoading = false;
                },
                loadedmetadata: () => {
                    playerStore.duration = el.duration;
                },
                play: () => {
                    playerStore.isPlaying = true;
                    if ('mediaSession' in navigator) {
                        navigator.mediaSession.playbackState = 'playing';
                    }
                    this.isTrackLoading = false;
                },
                pause: () => {
                    playerStore.isPlaying = false;
                    if ('mediaSession' in navigator) {
                        navigator.mediaSession.playbackState = 'paused';
                    }
                },
                ended: () => {
                    playerActions.handleTrackEnded();
                },
                error: (e) => {
                    console.error('Audio loading error:', e);
                    playerStore.isPlaying = false;
                    this.isTrackLoading = false;
                }
            };

            el.addEventListener('loadstart', this._audioHandlers.loadstart);
            el.addEventListener('waiting', this._audioHandlers.waiting);
            el.addEventListener('stalled', this._audioHandlers.stalled);
            el.addEventListener('canplay', this._audioHandlers.canplay);
            el.addEventListener('canplaythrough', this._audioHandlers.canplaythrough);
            el.addEventListener('loadeddata', this._audioHandlers.loadeddata);
            el.addEventListener('loadedmetadata', this._audioHandlers.loadedmetadata);
            el.addEventListener('play', this._audioHandlers.play);
            el.addEventListener('pause', this._audioHandlers.pause);
            el.addEventListener('ended', this._audioHandlers.ended);
            el.addEventListener('error', this._audioHandlers.error);
        },
        removeAudioEventListeners() {
            const el = this.$refs.audioEl;
            if (el && this._audioHandlers) {
                el.removeEventListener('loadstart', this._audioHandlers.loadstart);
                el.removeEventListener('waiting', this._audioHandlers.waiting);
                el.removeEventListener('stalled', this._audioHandlers.stalled);
                el.removeEventListener('canplay', this._audioHandlers.canplay);
                el.removeEventListener('canplaythrough', this._audioHandlers.canplaythrough);
                el.removeEventListener('loadeddata', this._audioHandlers.loadeddata);
                el.removeEventListener('loadedmetadata', this._audioHandlers.loadedmetadata);
                el.removeEventListener('play', this._audioHandlers.play);
                el.removeEventListener('pause', this._audioHandlers.pause);
                el.removeEventListener('ended', this._audioHandlers.ended);
                el.removeEventListener('error', this._audioHandlers.error);
                this._audioHandlers = null;
            }
        },

        setupSpaceKeyListener() {
            this.spaceKeyListener = (event) => {
                // Проверяем, что нет фокуса на элементах ввода
                if (event.target.matches('input, textarea, select, [contenteditable]')) {
                    return;
                }

                // Пробел - play/pause
                if (event.code === 'Space') {
                    event.preventDefault(); // Предотвращаем прокрутку страницы
                    this.togglePlay();
                }
                // Left - перемотка назад на 5 секунд
                else if (event.code === 'ArrowLeft') {
                    event.preventDefault();
                    const newTime = Math.max(0, this.currentTime - 5);
                    this.seekTo(newTime);
                }
                // Right - перемотка вперёд на 5 секунд
                else if (event.code === 'ArrowRight') {
                    event.preventDefault();
                    const newTime = Math.min(this.duration, this.currentTime + 5);
                    this.seekTo(newTime);
                }
            };
            document.addEventListener('keydown', this.spaceKeyListener);
        }
    }
}
</script>

<style scoped>

.progress-line {
  height: 2.5rem;
  width: 100%;
  opacity: 0.9;
  cursor: pointer;
  margin-left: 1rem;
  margin-right: 1rem;
  position: relative;
  overflow: hidden;
}

/* Unplayed waveform */
.mini_waveform_reveal::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: var(--waveform-bg);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  filter: invert(53.3333%);
}

.mini_waveform_reveal {
  position: absolute;
  inset: 0;
  pointer-events: none;
  animation: mini_waveform_reveal 650ms cubic-bezier(0.22, 1, 0.36, 1) both;
  will-change: clip-path;
}

@keyframes mini_waveform_reveal {
  from {
    clip-path: inset(0 100% 0 0);
  }

  to {
    clip-path: inset(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mini_waveform_reveal {
    animation: none;
  }
}

/* Played waveform */
.progress-fill {
  position: absolute;
  inset: 0;
  background-image: var(--waveform-bg);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  filter: invert(89.4118%);
  clip-path: inset(0 calc(100% - var(--progress-width, 0%)) 0 0);
  transition: clip-path 0.1s linear;
  pointer-events: none;
  z-index: 1;
}

.next-track-btn {
  font-size: 1.5rem;
  padding-left: 0.5rem;
  cursor: pointer;
}

.track-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.track-title {
  white-space: nowrap;
  overflow: hidden;
  font-weight: 600;
}

.track-title-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.track-title-text.is-scrollable {
  display: inline-block;
  max-width: none;
  overflow: visible;
  text-overflow: clip;
  animation: title-marquee var(--title-scroll-duration, 12s) ease-in-out infinite;
}

.artist-subtitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
  margin-top: 2px;
}

.p-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  ;
  position: relative;
}

.player-sec {
  flex-shrink: 0;
}

.edit-sec {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-bottom: 1.5rem;
  overflow: hidden;
}


.player-fixed {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

.player-fixed .player-container {
  width: 100%;
  /*height: 4rem;*/
  background: rgba(47, 47, 47, 0.55);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12.3px);
  -webkit-backdrop-filter: blur(12.3px);
  border: 1px solid rgba(47, 47, 47, 0.61);
  /*background-color: var(--secondary-bg-color);*/
  border-radius: 9999px;
  display: flex;
  margin-bottom: 1rem;
  /*box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px 0px;*/
  transition: transform 300ms cubic-bezier(.22, .9, .34, 1), opacity 220ms cubic-bezier(.22, .9, .34, 1);
  transform-origin: bottom center;
  position: relative;
  overflow: hidden;
}

.player-fixed .player-container.player-hidden {
  transform: translateY(18px);
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
}

.player-fixed .player-container.player-hidden * {
  user-select: none;
  -webkit-user-select: none;
  caret-color: transparent;
  overflow: hidden;
}

.player-line {
  display: flex;
  align-items: center;
}


.player {
  padding: 0.6rem 0.6rem;
  width: 100%;
}

.cover {
  width: 2.8rem;
  height: 2.8rem;
  box-sizing: border-box;
  border: 1px solid rgb(152 152 152 / 20%);
  border-radius: 50%;
  object-fit: cover;
  animation: spin var(--spin-duration, 2s) linear infinite;
  animation-play-state: paused;
  transition: opacity 220ms ease;
}

.cover.spinning {
  animation-play-state: running;
}

.cover.cover-loading {
  opacity: 0;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes title-marquee {
  0% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(calc(-1 * var(--title-scroll-distance, 0px)));
  }

  100% {
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .track-title-text.is-scrollable {
    animation: none;
  }
}

.cover-wrap {
  position: relative;
  width: 2.8rem;
  height: 2.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
}

.cover-loader {
  position: absolute;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.85);
  animation: spin 0.9s linear infinite;
  opacity: 0;
  transform: scale(0.9);
  transition: opacity 220ms ease, transform 220ms ease;
}

.cover-loader.visible {
  opacity: 1;
  transform: scale(1);
}

.cover-wrap::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 0.6rem;
  height: 0.6rem;
  background: rgba(47, 47, 47, 0.55);
  backdrop-filter: blur(12.3px);
  -webkit-backdrop-filter: blur(12.3px);
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
}

.cover-wrap.is-loading::before {
  opacity: 0;
}

.cover-wrap.is-loading :deep(.play-overlay) {
  opacity: 0;
  pointer-events: none;
}

/* when playing, overlay is hidden by default; reveal on hover */
.cover-wrap:hover .play-overlay.playing {
  opacity: 1;
  pointer-events: auto;
  transform: translate(-50%, -50%) scale(1);
}

.playbutton {
  font-size: 2.5rem;
  text-align: center;
  cursor: pointer;
}

.playbutton-toggle {
  color: var(--secondary-text-color);
  transition: color 160ms ease;
}

.playbutton-toggle.is-active {
  color: #d48001;
}

.buttonrow .row {
  align-items: center;
}

.bpm,
.key,
.lufs {
  font-family: "Doto", sans-serif;
  font-weight: 900;
  display: inline-block;
  margin-right: 12px;
  font-size: 14px;
  color: #bbb;
  background-color: #2f2f2f;
  padding: 4px 8px;
  border: 1px #2f2f2f;
  border-radius: 6px;
}

.calculated {
  background-color: #d48001 !important;
  color: #331f00 !important;
  border: 1px #331f00 !important;
}


</style>
